/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SUBSCRIPTION SERVICE - Subscription Plan Management & Limit Enforcement
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * This service manages subscription-based plan logic including:
 * - Fetching subscription info with plan limits from API
 * - Converting subscription limits to usable format
 * - Enforcing subscription-based limits (products, domains, features)
 * - Integration with existing planService
 * 
 * @author MyTamilKart Development Team
 * @version 1.0
 * @lastModified 2025-12-23
 */

import api from '../api/axiosInstance';
import { INTERNAL_TEAM_WHITELIST } from '../config/internalTeam';

// ═══════════════════════════════════════════════════════════════════════════
// SECTION 1: HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convert subscription limits array to object format
 * ---------------------------------------------------
 * Converts API limits format to planService-compatible format.
 * 
 * API format: [{ key: "max_products", value: 50 }, ...]
 * Output format: { maxProducts: 50, maxDomains: 1, ... }
 * 
 * @param {Array} limitsArray - Limits array from subscription API
 * @returns {Object} Converted limits object
 */
function convertSubscriptionLimits(limitsArray) {
  if (!Array.isArray(limitsArray)) {
    console.warn('[SUBSCRIPTION SERVICE] ⚠️ Limits is not an array:', limitsArray);
    return {};
  }

  const limits = {};
  
  limitsArray.forEach(limit => {
    const { key, value } = limit;
    
    // Convert snake_case to camelCase and map to planService format
    switch (key) {
      case 'max_products':
        limits.maxProducts = typeof value === 'string' ? parseInt(value) : value;
        break;
      case 'max_domains':
        // Handle "Unlimited Domains" string
        if (value === 'Unlimited Domains' || value === 'unlimited') {
          limits.maxDomains = -1;
        } else {
          limits.maxDomains = typeof value === 'string' ? parseInt(value) : value;
        }
        break;
      case 'analytics':
        limits.analytics = value;
        break;
      case 'discount_codes':
        limits.discountCodes = value === true || value === 'true';
        break;
      case 'multi_store':
        // Handle "Multi-store Management" string
        limits.multiStore = value === true || value === 'true' || value === 'Multi-store Management';
        break;
      case 'support':
        limits.support = value;
        break;
      default:
        console.warn(`[SUBSCRIPTION SERVICE] ⚠️ Unknown limit key: ${key}`);
    }
  });

  console.log('[SUBSCRIPTION SERVICE] ✅ Converted limits:', limits);
  return limits;
}

/**
 * Get fallback limits for basic/expired plans
 * --------------------------------------------
 * Returns read-only limits when subscription is inactive.
 */
function getBasicLimits() {
  return {
    maxProducts: 0,
    maxDomains: 0,
    analytics: 'none',
    discountCodes: false,
    multiStore: false,
    support: 'none',
    readOnly: true
  };
}

const subscriptionService = {

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 2: MAIN API FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get subscription information with plan limits
   * ----------------------------------------------
   * Fetches subscription info from /subscription/info API and converts
   * limits to planService-compatible format.
   * 
   * @param {string} businessUserId - Business user ID
   * @returns {Promise<Object>} Subscription info with converted limits
   * 
   * RETURNED OBJECT:
   * {
   *   has_subscription: boolean,
   *   subscription_id: string,
   *   status: string,
   *   plan_id: string,
   *   plan_name: string,
   *   limits: Object (converted to planService format),
   *   next_billing_date: string,
   *   paid_count: number,
   *   remaining_count: number,
   *   isReadOnly: boolean,
   *   hasActivePlan: boolean
   * }
   * 
   * @example
   * const subInfo = await getSubscriptionInfo('user123');
   * if (subInfo.hasActivePlan) {
   *   console.log('Max products:', subInfo.limits.maxProducts);
   * }
   */
  async getSubscriptionInfo(businessUserId) {
    try {
      console.log('[SUBSCRIPTION SERVICE] 🔄 Fetching subscription info for:', businessUserId);

      // Check if user is internal team (bypass all restrictions)
      if (INTERNAL_TEAM_WHITELIST.includes(businessUserId)) {
        console.log('[SUBSCRIPTION SERVICE] ✅ Internal team member - full access');
        return {
          has_subscription: true,
          status: 'active',
          plan_id: 'premium',
          plan_name: 'Internal Team',
          limits: {
            maxProducts: -1,
            maxDomains: -1,
            analytics: 'premium',
            discountCodes: true,
            multiStore: true,
            support: '24/7'
          },
          isReadOnly: false,
          hasActivePlan: true,
          isInternalTeam: true
        };
      }

      const response = await api.get('/subscription/info', {
        params: { business_user_id: businessUserId }
      });

      if (response.data.status === 'success') {
        const result = response.data.result;

        // Check if user has active subscription
        if (result.has_subscription && result.status === 'active') {
          console.log('[SUBSCRIPTION SERVICE] ✅ Active subscription found:', result.plan_name);

          // Convert limits from API format to planService format
          const convertedLimits = convertSubscriptionLimits(result.limits || []);

          return {
            ...result,
            limits: convertedLimits,
            isReadOnly: false,
            hasActivePlan: true,
            isInternalTeam: false
          };
        } else {
          console.log('[SUBSCRIPTION SERVICE] ❌ No active subscription');
          return {
            has_subscription: false,
            status: 'none',
            plan_id: 'basic',
            plan_name: 'No Plan',
            limits: getBasicLimits(),
            isReadOnly: true,
            hasActivePlan: false,
            isInternalTeam: false
          };
        }
      }

      throw new Error('Failed to fetch subscription info');
    } catch (error) {
      console.error('[SUBSCRIPTION SERVICE] ❌ Error fetching subscription:', error);
      
      // Return basic plan on error
      return {
        has_subscription: false,
        status: 'error',
        plan_id: 'basic',
        plan_name: 'Basic',
        limits: getBasicLimits(),
        isReadOnly: true,
        hasActivePlan: false,
        isInternalTeam: false
      };
    }
  },

  /**
   * Get available subscription plans
   * ---------------------------------
   * Fetches active subscription plans from /subscription/plans/list API.
   * 
   * @returns {Promise<Array>} Array of available subscription plans
   * 
   * @example
   * const plans = await getAvailablePlans();
   * plans.forEach(plan => {
   *   console.log(plan.plan_name, plan.price);
   * });
   */
  async getAvailablePlans() {
    try {
      console.log('[SUBSCRIPTION SERVICE] 🔄 Fetching available plans...');

      const response = await api.get('/subscription/plans/list');

      if (response.data.status === 'success' && response.data.result?.plans) {
        console.log('[SUBSCRIPTION SERVICE] ✅ Plans fetched:', response.data.result.plans.length);
        return response.data.result.plans;
      }

      return [];
    } catch (error) {
      console.error('[SUBSCRIPTION SERVICE] ❌ Error fetching plans:', error);
      return [];
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // SECTION 3: LIMIT ENFORCEMENT (Compatible with planService)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Check if user can add a product
   * --------------------------------
   * Validates if user has reached their product limit based on subscription.
   * 
   * @param {Object} subscriptionInfo - Subscription info from getSubscriptionInfo()
   * @param {number} currentProductCount - Current number of products
   * @returns {Object} { allowed: boolean, reason: string }
   * 
   * @example
   * const check = canAddProduct(subInfo, 45);
   * if (!check.allowed) {
   *   toast.error(check.reason);
   * }
   */
  canAddProduct(subscriptionInfo, currentProductCount) {
    // Internal team has unlimited access
    if (subscriptionInfo?.isInternalTeam) {
      return { allowed: true };
    }

    if (subscriptionInfo?.isReadOnly || !subscriptionInfo?.hasActivePlan) {
      return { 
        allowed: false, 
        reason: 'No active subscription. Please subscribe to add products.' 
      };
    }

    const limits = subscriptionInfo.limits;
    
    // Unlimited products
    if (limits.maxProducts === -1) {
      return { allowed: true };
    }

    if (currentProductCount >= limits.maxProducts) {
      return { 
        allowed: false, 
        reason: `Product limit reached (${limits.maxProducts}). Upgrade your plan to add more.` 
      };
    }

    return { allowed: true };
  },

  /**
   * Check if user can add a domain
   * -------------------------------
   * Validates if user has reached their domain limit based on subscription.
   * 
   * @param {Object} subscriptionInfo - Subscription info from getSubscriptionInfo()
   * @param {number} currentDomainCount - Current number of domains
   * @returns {Object} { allowed: boolean, reason: string }
   * 
   * @example
   * const check = canAddDomain(subInfo, 1);
   * if (!check.allowed) {
   *   toast.error(check.reason);
   * }
   */
  canAddDomain(subscriptionInfo, currentDomainCount) {
    // Internal team has unlimited access
    if (subscriptionInfo?.isInternalTeam) {
      return { allowed: true };
    }

    if (subscriptionInfo?.isReadOnly || !subscriptionInfo?.hasActivePlan) {
      return { 
        allowed: false, 
        reason: 'No active subscription. Please subscribe to add domains.' 
      };
    }

    const limits = subscriptionInfo.limits;
    
    // Unlimited domains
    if (limits.maxDomains === -1) {
      return { allowed: true };
    }

    if (currentDomainCount >= limits.maxDomains) {
      return { 
        allowed: false, 
        reason: `Domain limit reached (${limits.maxDomains}). Upgrade your plan to add more.` 
      };
    }

    return { allowed: true };
  },

  /**
   * Check if a feature is available
   * --------------------------------
   * Determines if a specific feature is available in the subscription.
   * 
   * @param {Object} subscriptionInfo - Subscription info from getSubscriptionInfo()
   * @param {string} feature - Feature name to check
   * @returns {boolean} True if feature is available
   * 
   * AVAILABLE FEATURES:
   * - addProduct, editProduct, deleteProduct
   * - discountCodes
   * - advancedAnalytics, premiumAnalytics
   * - multiStore
   * - unlimitedDomains
   * 
   * @example
   * if (hasFeature(subInfo, 'discountCodes')) {
   *   // Show discount code feature
   * }
   */
  hasFeature(subscriptionInfo, feature) {
    // Internal team has access to everything
    if (subscriptionInfo?.isInternalTeam) {
      return true;
    }

    if (!subscriptionInfo || subscriptionInfo.isReadOnly || !subscriptionInfo.hasActivePlan) {
      return false;
    }

    const limits = subscriptionInfo.limits;

    switch (feature) {
      case 'addProduct':
      case 'editProduct':
      case 'deleteProduct':
        return !limits.readOnly;
      case 'discountCodes':
        return limits.discountCodes === true;
      case 'advancedAnalytics':
        return limits.analytics === 'advanced' || 
               limits.analytics === 'Advanced Analytics' ||
               limits.analytics === 'premium' ||
               limits.analytics === 'Premium Analytics';
      case 'premiumAnalytics':
        return limits.analytics === 'premium' || limits.analytics === 'Premium Analytics';
      case 'multiStore':
        return limits.multiStore === true;
      case 'unlimitedDomains':
        return limits.maxDomains === -1;
      default:
        return false;
    }
  },

  /**
   * Get upgrade message for a feature
   * ----------------------------------
   * Returns a user-friendly message for locked features.
   * 
   * @param {string} feature - Feature name
   * @returns {string} Upgrade message
   * 
   * @example
   * const message = getUpgradeMessage('discountCodes');
   * // Returns: "Upgrade to Pro or Premium to use discount codes"
   */
  getUpgradeMessage(feature) {
    const messages = {
      'addProduct': 'Subscribe to a plan to add products',
      'discountCodes': 'Upgrade to Pro or Premium to use discount codes',
      'advancedAnalytics': 'Upgrade to Pro or Premium for advanced analytics',
      'multiStore': 'Upgrade to Premium for multi-store management',
      'unlimitedDomains': 'Upgrade to Premium for unlimited domains'
    };

    return messages[feature] || 'Upgrade your subscription to access this feature';
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════

export default subscriptionService;
